import { PersonFilterInput, PersonPaginationSort } from '../gql/graphql';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  toPagination,
} from './utils';

import { convergeLogger } from '@converge/config';
import { graphql } from '../gql';
import moment from 'moment';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_ENTITIES_BROKERS = graphql(`
  query getBrokers(
    $filters: PersonFilterInput
    $pagination: PaginationRequest
    $sort: PersonPaginationSort
  ) {
    persons(filters: $filters, pagination: $pagination, sort: $sort) {
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
      edges {
        cursor
        node {
          uuid
          createdAt
          firstName
          lastName
          agent
          title
          status
          source
          uwTerritories {
            edges {
              node {
                name
              }
            }
          }
          groups {
            edges {
              node {
                name
                label
              }
            }
          }
          office {
            name
            address {
              uuid
              street1
              street2
              city
              type
              postalCode
              source
              regions {
                edges {
                  cursor
                  node {
                    uuid
                    name
                    iso2Code
                  }
                }
              }
            }
          }
          licenses {
            pageInfo {
              totalResults
            }
          }
          teams {
            edges {
              node {
                uuid
                name
              }
            }
          }
          businessDevManagement {
            firstName
            lastName
            uuid
          }
          underwriterManagement {
            firstName
            lastName
            uuid
          }
          entities {
            edges {
              node {
                uuid
                referenceId
                addresses(filters: { source: PRIMARY }) {
                  edges {
                    cursor
                    node {
                      uuid
                      street1
                      street2
                      city
                      type
                      postalCode
                      source
                      regions {
                        edges {
                          cursor
                          node {
                            uuid
                            name
                            iso2Code
                          }
                        }
                      }
                      primary
                    }
                  }
                }
                aliases(filters: { type: LEGAL_NAME, source: PRIMARY }) {
                  edges {
                    cursor
                    queryCursor
                    node {
                      uuid
                      name
                      type
                      sources
                      primary
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);
export function useGetBrokers({
  pageNumber = 0,
  pageSize = 6,
  uuid,
  isAgency = true,
  sort,
  filters,
  enabled = true,
}: {
  pageNumber?: number;
  pageSize?: number;
  uuid?: string;
  isAgency?: boolean;
  sort?: PersonPaginationSort;
  filters?: PersonFilterInput;
  enabled?: boolean;
}) {
  const variables: {
    pagination: { first: number; after: number };
    filters: PersonFilterInput;
    sort?: PersonPaginationSort;
  } = {
    pagination: toPagination(pageNumber, pageSize),
    filters: {
      ...filters,
      agent: isAgency,
    },
  };

  if (sort && sort.key) {
    variables.sort = sort;
  }
  if (uuid) {
    variables.filters.uuid = uuid;
  }
  const { loading, error, data, fetchMore } = useQuery(GET_ENTITIES_BROKERS, {
    variables,
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const brokers = useMemo(() => {
    if (!data || !data.persons || !data.persons.edges) return [];

    return (
      getPaginatedNodes(data.persons)
        // .filter((e) => e.agency)
        .map((edge) => {
          const entity = getPaginatedNodes(edge.entities);

          const alias = getPaginatedNodes(entity[0]?.aliases);
          // .find(
          //   ({ type, primary }) => type === AliasType.LegalName && primary
          // );

          const primaryAddress = entity[0]?.addresses?.edges?.find((item) => {
            const node = item?.node;
            return node?.type?.includes('CORPORATE');
          });

          if (!alias) {
            convergeLogger.error(
              `No primary legal name alias found for broker ${edge.uuid}`
            );
          }

          const bdmUser = edge?.businessDevManagement;
          const uwUser = edge?.underwriterManagement;

          return {
            id: edge?.uuid ?? '\u2014',
            value: edge?.uuid ?? '\u2014',
            label: `${edge?.firstName} ${edge?.lastName}` || '',
            broker: `${edge?.firstName} ${edge?.lastName}` || '',
            brokerage: alias[0]?.name ?? '\u2014',
            team: edge?.teams?.edges[0]?.node?.name ?? '\u2014',
            licenses: edge?.licenses?.pageInfo?.totalResults ?? '0',
            primaryAddress: primaryAddress,
            uwRegion: edge?.uwTerritories?.edges[0]?.node?.name ?? '\u2014',

            city: edge?.office?.name
              ? edge?.office?.name
              : primaryAddress?.node.city,
            region: getFirstPaginatedNode(entity[0]?.addresses)?.regions
              ?.edges[0]?.node?.name,
            onboarded: moment(edge?.createdAt?.split('T')[0]).format(
              'MM/DD/YYYY'
            ),
            status: edge?.status ?? '—',
            brokerageId: entity[0]?.uuid ?? '',
            underwriterName: uwUser
              ? `${uwUser.firstName} ${uwUser.lastName}`
              : '—',
            bdmName: bdmUser ? `${bdmUser.firstName} ${bdmUser.lastName}` : '—',
          };
        })
    );
  }, [data]);

  return {
    error,
    loading,
    data,
    brokers,
    fetchMore,
    hasNextPage: data?.persons?.pageInfo?.hasNextPage ?? false,
  };
}
